import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../../contexts/AuthContext'
import { usersApi } from '../../services/api'
import toast from 'react-hot-toast'
import {
  TrophyIcon,
  CheckCircleIcon,

  InformationCircleIcon,

  UsersIcon,
  UserIcon
} from '@heroicons/react/24/outline'

// Competition categories and age groups (matches backend)
const COMPETITIONS = [
  { id: 'coptic_hymns', name: 'Coptic Hymns', category: 'Spiritual', type: 'individual', minAge: 6, maxAge: 100 },
  { id: 'coptic_language', name: 'Coptic Language', category: 'Spiritual', type: 'individual', minAge: 9, maxAge: 100 },
  { id: 'spiritual_knowledge', name: 'Spiritual Knowledge', category: 'Spiritual', type: 'individual', minAge: 6, maxAge: 100 },
  { id: 'art_creativity', name: 'Art & Creativity', category: 'Arts', type: 'individual', minAge: 6, maxAge: 100 },
  { id: 'soccer', name: 'Soccer', category: 'Sports', type: 'team', minAge: 9, maxAge: 100 },
  { id: 'volleyball', name: 'Volleyball', category: 'Sports', type: 'team', minAge: 12, maxAge: 100 },
  { id: 'chess', name: 'Chess', category: 'Sports', type: 'individual', minAge: 9, maxAge: 100 },
  { id: 'table_tennis', name: 'Table Tennis', category: 'Sports', type: 'individual', minAge: 9, maxAge: 100 }
]

const AGE_GROUPS = [
  { id: '6-8', name: '6-8 years', min: 6, max: 8 },
  { id: '9-11', name: '9-11 years', min: 9, max: 11 },
  { id: '12-14', name: '12-14 years', min: 12, max: 14 },
  { id: '15-17', name: '15-17 years', min: 15, max: 17 },
  { id: '18+', name: '18+ years', min: 18, max: 100 }
]

const competitionSchema = z.object({
  selectedCompetitions: z.array(z.string()).min(1, 'Please select at least one competition'),
  medicalConditions: z.string().optional(),
  dietaryRestrictions: z.string().optional(),
  emergencyContact: z.string().min(2, 'Emergency contact name is required'),
  emergencyPhone: z.string().min(10, 'Emergency contact phone is required'),
  parentConsent: z.boolean().refine(val => val === true, 'Parent/guardian consent is required for participants under 18'),
  codeOfConduct: z.boolean().refine(val => val === true, 'Agreement to code of conduct is required')
})

type CompetitionForm = z.infer<typeof competitionSchema>

const CompetitionRegistration: React.FC = () => {
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [userAge, setUserAge] = useState<number | null>(null)
  const [eligibleCompetitions, setEligibleCompetitions] = useState<typeof COMPETITIONS>([])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<CompetitionForm>({
    resolver: zodResolver(competitionSchema)
  })

  const selectedCompetitions = watch('selectedCompetitions', [])

  useEffect(() => {
    if (user?.dateOfBirth) {
      const today = new Date()
      const birthDate = new Date(user.dateOfBirth)
      const age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        setUserAge(age - 1)
      } else {
        setUserAge(age)
      }
    }
  }, [user])

  useEffect(() => {
    if (userAge !== null) {
      const eligible = COMPETITIONS.filter(comp =>
        userAge >= comp.minAge && userAge <= comp.maxAge
      )
      setEligibleCompetitions(eligible)
    }
  }, [userAge])

  const getUserAgeGroup = () => {
    if (userAge === null) return null
    return AGE_GROUPS.find(group => userAge >= group.min && userAge <= group.max)
  }

  const onSubmit = async (data: CompetitionForm) => {
    if (!user) return

    setIsSubmitting(true)
    try {
      // Register for each selected competition
      for (const competitionId of data.selectedCompetitions) {
        await usersApi.registerParticipant({
          competitionId,
          userId: user.id,
          medicalConditions: data.medicalConditions,
          dietaryRestrictions: data.dietaryRestrictions,
          emergencyContact: data.emergencyContact,
          emergencyPhone: data.emergencyPhone,
          ageGroup: getUserAgeGroup()?.id
        })
      }

      setHasSubmitted(true)
      toast.success('Competition registration submitted successfully!')
    } catch (error: any) {
      console.error('Competition registration failed:', error)
      toast.error(error.response?.data?.error || 'Failed to register for competitions')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-8 text-center">
        <InformationCircleIcon className="mx-auto h-12 w-12 text-blue-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Authentication Required</h2>
        <p className="text-gray-600 mb-6">
          You must be logged in to register for competitions. Please create an account or sign in to continue.
        </p>
        <div className="flex justify-center space-x-4">
          <a
            href="/register"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Account
          </a>
          <a
            href="/login"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Sign In
          </a>
        </div>
      </div>
    )
  }

  if (hasSubmitted) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-8 text-center">
        <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-6" />
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Submitted!</h2>
        <p className="text-gray-600 mb-6">
          Your competition registration has been submitted successfully. Your church coordinator will review and approve your registrations.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 mb-2">What happens next?</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Your church coordinator will review your registration</li>
                <li>• You'll receive email updates on approval status</li>
                <li>• Approved participants will receive competition schedules</li>
                <li>• Make sure to attend any required practice sessions</li>
              </ul>
            </div>
          </div>
        </div>
        <a
          href="/participant/profile"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          View My Profile
        </a>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center">
          <TrophyIcon className="h-10 w-10 text-blue-600 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Competition Registration</h1>
            <p className="text-gray-600 mt-1">
              Register for Mahragan competitions - {user.firstName} {user.lastName}
              {userAge && ` (Age: ${userAge})`}
            </p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Participant Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Name:</span>
            <p className="font-medium">{user.firstName} {user.lastName}</p>
          </div>
          <div>
            <span className="text-gray-500">Age Group:</span>
            <p className="font-medium">{getUserAgeGroup()?.name || 'Unknown'}</p>
          </div>
          <div>
            <span className="text-gray-500">Church:</span>
            <p className="font-medium">Church ID: {user.churchId}</p>
          </div>
        </div>
      </div>

      {/* Competition Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Select Competitions</h2>

            {eligibleCompetitions.length === 0 ? (
              <div className="text-center py-8">
                <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No competitions available for your age group.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {eligibleCompetitions.map((competition) => (
                  <label key={competition.id} className="relative">
                    <input
                      type="checkbox"
                      {...register('selectedCompetitions')}
                      value={competition.id}
                      className="sr-only peer"
                    />
                    <div className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer transition-colors peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:border-gray-300">
                      <div className="flex items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 flex items-center">
                            {competition.type === 'team' ? (
                              <UsersIcon className="h-4 w-4 mr-2 text-purple-500" />
                            ) : (
                              <UserIcon className="h-4 w-4 mr-2 text-blue-500" />
                            )}
                            {competition.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">{competition.category}</p>
                          <div className="flex items-center mt-2">
                            <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                              competition.type === 'team'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {competition.type}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:border-blue-500 peer-checked:bg-blue-500 flex items-center justify-center">
                            {selectedCompetitions?.includes(competition.id) && (
                              <CheckCircleIcon className="w-3 h-3 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {errors.selectedCompetitions && (
              <p className="mt-2 text-sm text-red-600">{errors.selectedCompetitions.message}</p>
            )}
          </div>

          {/* Medical & Emergency Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Medical & Emergency Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medical Conditions (Optional)
                </label>
                <textarea
                  {...register('medicalConditions')}
                  rows={3}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List any medical conditions, allergies, or medications..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dietary Restrictions (Optional)
                </label>
                <textarea
                  {...register('dietaryRestrictions')}
                  rows={3}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List any dietary restrictions or food allergies..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact Name *
                </label>
                <input
                  {...register('emergencyContact')}
                  type="text"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Emergency contact person"
                />
                {errors.emergencyContact && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyContact.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact Phone *
                </label>
                <input
                  {...register('emergencyPhone')}
                  type="tel"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
                {errors.emergencyPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.emergencyPhone.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Consent & Agreement */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Consent & Agreement</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <input
                  {...register('parentConsent')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                />
                <div className="ml-3">
                  <label className="text-sm font-medium text-gray-700">
                    Parent/Guardian Consent *
                  </label>
                  <p className="text-sm text-gray-600">
                    I confirm that as a parent/guardian, I consent to my child's participation in the selected competitions. (Required for participants under 18)
                  </p>
                </div>
              </div>
              {errors.parentConsent && (
                <p className="text-sm text-red-600 ml-7">{errors.parentConsent.message}</p>
              )}

              <div className="flex items-start">
                <input
                  {...register('codeOfConduct')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                />
                <div className="ml-3">
                  <label className="text-sm font-medium text-gray-700">
                    Code of Conduct Agreement *
                  </label>
                  <p className="text-sm text-gray-600">
                    I agree to abide by the Mahragan code of conduct, emphasizing Christian values, good sportsmanship, and respectful behavior.
                  </p>
                </div>
              </div>
              {errors.codeOfConduct && (
                <p className="text-sm text-red-600 ml-7">{errors.codeOfConduct.message}</p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t">
            <button
              type="submit"
              disabled={isSubmitting || selectedCompetitions?.length === 0}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Register for Selected Competitions'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CompetitionRegistration